import { useMemo } from 'react'
import { BarChart3, Activity } from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import { useRideIntelligence } from '@/hooks/useRideIntelligence'
import { RideIntelligenceCard, RideIntelligenceCardSkeleton } from '@/components/analytics/RideIntelligenceCard'
import { RankingsPanel } from '@/components/analytics/RankingsPanel'

export function RideIntelligencePage() {
  const { currentEventId, events } = useAuth()
  const { data, loading } = useRideIntelligence(currentEventId)
  const activeEvent = events.find((e) => e.EventID === currentEventId)

  const classificationMap = useMemo(() => {
    if (!data?.classifications) return new Map()
    return new Map(data.classifications.map(c => [c.attraction_id, c]))
  }, [data?.classifications])

  if (!currentEventId) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <p className="text-ink-soft dark:text-slate-400">Select an event to view ride intelligence.</p>
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-7xl space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-butler-50 dark:bg-butler-900/30">
            <BarChart3 className="h-5 w-5 text-butler-600 dark:text-butler-300" />
          </div>
          <div>
            <h1 className="font-display text-xl font-bold text-ink dark:text-slate-100">
              Ride Intelligence
            </h1>
            <p className="text-xs text-ink-soft dark:text-slate-400">
              {activeEvent?.EventName || `Event ${currentEventId}`} — estimated earnings, classifications &amp; rankings
            </p>
          </div>
        </div>
      </div>

      {/* Scan values summary */}
      {data?.scan_values && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <SummaryCard
            label="ULR Value / Scan"
            value={data.scan_values.ulr.avg_scan_value != null ? `$${data.scan_values.ulr.avg_scan_value.toFixed(2)}` : '—'}
            sub={`${(data.scan_values.ulr.total_scans || 0).toLocaleString()} ULR scans`}
          />
          <SummaryCard
            label="FP Value / Scan"
            value={data.scan_values.fast_pass.avg_scan_value != null ? `$${data.scan_values.fast_pass.avg_scan_value.toFixed(2)}` : '—'}
            sub={`${(data.scan_values.fast_pass.total_scans || 0).toLocaleString()} FP scans`}
          />
          <SummaryCard
            label="Breakage Rate"
            value={data.scan_values.breakage.breakage_rate != null ? `${(data.scan_values.breakage.breakage_rate * 100).toFixed(1)}%` : '—'}
            sub={`${(data.scan_values.breakage.breakage_credits || 0).toLocaleString()} unused credits`}
          />
          <SummaryCard
            label="Total ULR Revenue"
            value={`$${(data.scan_values.ulr.total_revenue || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`}
            sub="attributed from plan isolation"
          />
        </div>
      )}

      {/* Type split */}
      {data?.type_split && (
        <div className="flex flex-wrap gap-2">
          {Object.entries(data.type_split).map(([type, split]) => (
            <div
              key={type}
              className="flex items-center gap-2 rounded-lg border border-hairline bg-white px-3 py-1.5 text-xs dark:border-slate-700 dark:bg-slate-800"
            >
              <Activity className="h-3.5 w-3.5 text-ink-soft dark:text-slate-400" />
              <span className="font-medium text-ink dark:text-slate-200">{type}</span>
              <span className="text-ink-soft dark:text-slate-400">
                {split.count} · {split.total_scans.toLocaleString()} scans · est. {split.estimated_earnings.toLocaleString()} credits
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Ride cards grid */}
      <section>
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-ink-soft dark:text-slate-400">
          Attractions ({data?.attractions.length ?? 0})
        </h2>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {loading
            ? Array.from({ length: 6 }, (_, i) => <RideIntelligenceCardSkeleton key={i} />)
            : data?.attractions.map((a, i) => (
                <RideIntelligenceCard
                  key={a.attraction_id}
                  attraction={a}
                  classification={classificationMap.get(a.attraction_id)}
                  rank={i + 1}
                />
              ))
          }
        </div>
      </section>

      {/* Rankings panel */}
      {data && (
        <section>
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-ink-soft dark:text-slate-400">
            Rankings
          </h2>
          <RankingsPanel
            attractions={data.attractions}
            dimensions={data.dimensions}
            loading={loading}
          />
        </section>
      )}

      {/* Records */}
      {data?.records && data.records.length > 0 && (
        <section>
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-ink-soft dark:text-slate-400">
            Records
          </h2>
          <div className="space-y-2">
            {data.records.map((r, i) => (
              <div
                key={i}
                className="flex items-center gap-3 rounded-lg border border-hairline bg-white px-4 py-2 dark:border-slate-700 dark:bg-slate-800"
              >
                <span className="text-lg">{isRecent(r.recorded_at) ? '⭐' : '📊'}</span>
                <div className="flex-1">
                  <span className="font-medium text-ink dark:text-slate-100">{r.attraction_name}</span>
                  <span className="mx-1 text-ink-soft dark:text-slate-400">—</span>
                  <span className="text-sm text-ink-soft dark:text-slate-400">
                    {r.record_type.replace(/_/g, ' ')}: {r.new_value.toLocaleString()}
                    {r.previous_value > 0 && ` (was ${r.previous_value.toLocaleString()}, +${r.improvement.toLocaleString()})`}
                  </span>
                </div>
                <span className="text-xs text-ink-soft dark:text-slate-400">
                  {r.event_name || `Event ${r.event_id}`}
                </span>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  )
}

function SummaryCard({ label, value, sub }: { label: string; value: string; sub: string }) {
  return (
    <div className="rounded-card border border-hairline bg-white p-3 shadow-card-soft dark:border-slate-700 dark:bg-slate-800">
      <div className="text-[10px] font-semibold uppercase tracking-wider text-ink-soft dark:text-slate-400">{label}</div>
      <div className="mt-1 font-mono text-lg font-bold text-butler-700 dark:text-butler-300">{value}</div>
      <div className="mt-0.5 text-[10px] text-ink-soft dark:text-slate-400">{sub}</div>
    </div>
  )
}

function isRecent(dateStr: string): boolean {
  const d = new Date(dateStr)
  const now = new Date()
  return (now.getTime() - d.getTime()) < 7 * 24 * 60 * 60 * 1000
}
